import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { keycloakService } from '../auth/keycloakService';
import { useAuth } from '../auth/useAuth';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const processingRef = useRef(false);

  useEffect(() => {
    // Prevent double processing (e.g., in React Strict Mode)
    if (processingRef.current) {
      console.log('CallbackPage: Already processing callback, skipping...');
      return;
    }

    const handleCallback = async () => {
      processingRef.current = true;
      try {
        // Check if we have the necessary URL parameters (code or error)
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        
        if (error) {
          console.error('CallbackPage: OAuth error from Keycloak:', error, urlParams.get('error_description'));
          processingRef.current = false;
          navigate('/login', { replace: true, state: { error: error } });
          return;
        }
        
        if (!code) {
          console.warn('CallbackPage: No authorization code found in URL. Redirecting to login.');
          processingRef.current = false;
          navigate('/login', { replace: true });
          return;
        }

        // Read redirect path BEFORE processing callback (in case something clears it)
        // Check both sessionStorage and localStorage as backup
        const redirectPathBefore = sessionStorage.getItem('keycloak_redirect_path') || 
                                   localStorage.getItem('keycloak_redirect_path');
        console.log('CallbackPage: Redirect path BEFORE callback processing:', redirectPathBefore);
        
        await keycloakService.handleCallback();
        await refreshUser();

        // If this tab was opened by another tab (e.g. the "log in to post"
        // flow on /posts/new), the original tab is responsible for resuming
        // the user's action via a storage event. Just close this tab.
        const openedFromAnotherTab =
          typeof window !== 'undefined' &&
          window.opener !== null &&
          window.opener !== window;
        if (openedFromAnotherTab) {
          sessionStorage.removeItem('keycloak_redirect_path');
          localStorage.removeItem('keycloak_redirect_path');
          window.close();
          return;
        }

        // Get redirect path from sessionStorage, localStorage, or default to home
        // Try reading again in case it was cleared
        const storedRedirectPath =
          sessionStorage.getItem('keycloak_redirect_path') ||
          localStorage.getItem('keycloak_redirect_path') ||
          redirectPathBefore ||
          '/';
        // Prevent login loops by never redirecting back into auth callback routes.
        const redirectPath =
          storedRedirectPath.startsWith('/callback') ||
          storedRedirectPath.startsWith('/silent-renew')
            ? '/'
            : storedRedirectPath;
        console.log('CallbackPage: Redirect path from sessionStorage AFTER callback:', sessionStorage.getItem('keycloak_redirect_path'));
        console.log('CallbackPage: Redirect path from localStorage:', localStorage.getItem('keycloak_redirect_path'));
        console.log('CallbackPage: Using redirect path:', redirectPath);
        sessionStorage.removeItem('keycloak_redirect_path');
        localStorage.removeItem('keycloak_redirect_path');
        
        console.log('CallbackPage: Navigating to:', redirectPath);
        navigate(redirectPath, { replace: true });
      } catch (error) {
        console.error('Error handling callback:', error);
        // Reset processing flag on error so user can retry
        processingRef.current = false;
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, refreshUser]);

  return (
    <div className="callback-page">
      <LoadingSpinner size="large" />
      <p>Completing login...</p>
    </div>
  );
};

